import { getErrorString } from "../../common/utilities/errorDetails";

// Wave 1 (Oct 19 2026): one Cognito error map for Log in, Forgot Password,
// Reset Password and New Password. Every code gets text, so the red error box
// is never empty. Amplify sets the Cognito code on `code` (and `name`).

export type SignInErrorStrings = {
  networkError: string;
  invalidCredentials: string;
  accessDenied: string;
  wrongChamber: (chamberName: string) => string;
  accessTurnedOff: (chamberName: string) => string;
  accessTurnedOffStaff: string;
  tooManyAttempts: string;
  passwordResetRequired: string;
  loginFailed: (chamberName: string) => string;
  loginFailedStaff: string;
};

export type PasswordResetErrorStrings = {
  networkError: string;
  emailNotFound: string;
  resetNotAvailable: string;
  tooManyAttempts: string;
  sendCodeFailed: string;
  codeIncorrect: string;
  codeExpired: string;
  passwordRule: string;
  resetFailed: string;
};

/** Member login pages name the chamber; the legacy /login page is for chamber staff. */
export type SignInAudience = { chamberName: string } | "staff";

export const getAuthErrorCode = (error: unknown): string =>
  getErrorString(error, "code") ||
  (error && typeof error === "object" && typeof (error as { name?: unknown }).name === "string"
    ? (error as { name: string }).name
    : "");

const TOO_MANY_ATTEMPT_CODES = new Set([
  "LimitExceededException",
  "TooManyRequestsException",
  "TooManyFailedAttemptsException",
]);

export const getSignInErrorText = (
  error: unknown,
  strings: SignInErrorStrings,
  audience: SignInAudience
): string => {
  const code = getAuthErrorCode(error);
  const message = getErrorString(error, "message") || "";
  const chamberName = audience === "staff" ? "" : audience.chamberName;
  const accessTurnedOff =
    audience === "staff" ? strings.accessTurnedOffStaff : strings.accessTurnedOff(chamberName);

  if (code === "NotAuthorizedException") {
    // Cognito sends one code for 3 causes; only the message tells them apart.
    if (/attempts exceeded/i.test(message)) return strings.tooManyAttempts;
    if (/disabled/i.test(message)) return accessTurnedOff;
    return strings.invalidCredentials;
  }
  if (TOO_MANY_ATTEMPT_CODES.has(code)) return strings.tooManyAttempts;
  switch (code) {
    case "NetworkError":
      return strings.networkError;
    case "UserNotFoundException":
      return strings.invalidCredentials;
    case "PasswordResetRequiredException":
      return strings.passwordResetRequired;
    case "CapUserTryingToSignIn":
      return strings.accessDenied;
    case "UserDisabled":
      return accessTurnedOff;
    case "WrongChamber":
      return strings.wrongChamber(chamberName);
    default:
      return audience === "staff" ? strings.loginFailedStaff : strings.loginFailed(chamberName);
  }
};

/** Forgot Password (send the code) and Reset Password (code and new password). */
export const getPasswordResetErrorText = (
  error: unknown,
  strings: PasswordResetErrorStrings,
  step: "sendCode" | "resetPassword"
): string => {
  const code = getAuthErrorCode(error);
  const message = getErrorString(error, "message") || "";
  if (code === "NetworkError") return strings.networkError;
  if (TOO_MANY_ATTEMPT_CODES.has(code)) return strings.tooManyAttempts;
  if (code === "UserNotFoundException") return strings.emailNotFound;

  if (step === "sendCode") {
    // Cognito does not allow self-service recovery before the first sign-in
    // with a temporary password is complete.
    if (code === "NotAuthorizedException" || code === "InvalidParameterException") {
      return strings.resetNotAvailable;
    }
    return strings.sendCodeFailed;
  }

  switch (code) {
    case "CodeMismatchException":
      return strings.codeIncorrect;
    case "ExpiredCodeException":
      return strings.codeExpired;
    case "InvalidPasswordException":
      return strings.passwordRule;
    case "InvalidParameterException":
      return /password/i.test(message) ? strings.passwordRule : strings.resetFailed;
    case "NotAuthorizedException":
      return strings.resetNotAvailable;
    default:
      return strings.resetFailed;
  }
};
